import { Platform } from 'react-native';
import { router } from 'expo-router';
import { useAuthStore, useModalStore } from '@/store';
import { checkIsIapSubscriber, hasValidPlusAccess } from '@/lib/purchaseUtils';
import { trackEvent } from '@/constants/utils/analytics';
import {
  PLUS_MONTHLY_CREDITS,
  PLUS_PREMIUM_MONTHLY_CREDITS,
} from '@/constants/config';

/** Slug of the default text model that costs Plus subscribers 0 credits in-app. */
export const ARYA_MODEL_SLUG = 'arya';

/** Route that opens a fresh chat with Arya preselected (accepts a slug). */
export const ARYA_CHAT_ROUTE = `/m/${ARYA_MODEL_SLUG}`;

/**
 * Pure summary of what a subscriber who has run out of credits can do.
 * Kept free of React so it can be unit-tested and reused from stores/handlers.
 *
 * @param {Object} user auth-store user
 * @returns {{
 *   isPlus: boolean,
 *   isPlusPremium: boolean,
 *   canUpgradeToPremium: boolean,
 *   totalAvailable: number,
 *   monthlyTotal: number,
 *   resetDate: Date|null,
 * }}
 */
export function getPlusCreditOptions(user) {
  const flags = user?.flags || {};
  const isPlus = !!flags.isPlus;
  const isPlusPremium = !!flags.isPlusPremium;
  const hasValidPlus = hasValidPlusAccess({
    isPlus,
    isComped: !!flags.isComped,
    plusExpiresAt: user?.plusExpiresAt,
    purchase: user?.activePurchase,
  });
  // Mirrors PricingModal: Plus Premium uses hosted billing and never stacks on an
  // App Store / Play subscription.
  const canUpgradeToPremium = hasValidPlus
    && !isPlusPremium
    && (Platform.OS === 'web' || Platform.OS === 'macos')
    && !checkIsIapSubscriber(user?.activePurchase);

  const rawReset = user?.credits?.monthlyAllotment?.resetDate || null;
  const parsed = rawReset ? new Date(rawReset) : null;
  const resetDate = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

  const allotmentTotal = Number(user?.credits?.monthlyAllotment?.total);
  const monthlyTotal = Number.isFinite(allotmentTotal) && allotmentTotal > 0
    ? allotmentTotal
    : (isPlusPremium ? PLUS_PREMIUM_MONTHLY_CREDITS : PLUS_MONTHLY_CREDITS);

  return {
    isPlus,
    isPlusPremium,
    canUpgradeToPremium,
    totalAvailable: Number(user?.credits?.totalAvailable) || 0,
    monthlyTotal,
    resetDate,
  };
}

/** "Sep 14" style label; includes the year when the reset is in a different year. */
export function formatCreditResetDate(date, now = new Date()) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

/** Whole days until the reset (never negative). */
export function daysUntilCreditReset(date, now = new Date()) {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.ceil((d.getTime() - now.getTime()) / 86400000));
}

/**
 * One-click "keep chatting with Arya": closes any open modal and lands on a
 * fresh chat with Arya preselected. Arya costs Plus subscribers nothing, so
 * this is the always-available escape hatch when the wallet is empty.
 */
export function startAryaChat({ source = 'out_of_credits', navigate } = {}) {
  trackEvent('conversion', 'out_of_credits_chat_arya', source);
  try {
    useModalStore.getState().actions.closeModal();
  } catch (_) { /* store may be unavailable in isolated callers */ }
  const push = typeof navigate === 'function' ? navigate : (path) => router.push(path);
  push(ARYA_CHAT_ROUTE);
}

export function openBuyCreditsForPlus({ source = 'out_of_credits' } = {}) {
  trackEvent('conversion', 'out_of_credits_buy_credits', source);
  useModalStore.getState().actions.openModal('CreditsPurchaseModal', { initialTab: 'buy-credits' });
}

/**
 * PricingModal detects an active Plus subscription and opens in its Premium
 * upgrade state (Plus card hidden, Premium preselected).
 */
export function openPremiumUpgrade({ source = 'out_of_credits' } = {}) {
  trackEvent('conversion', 'out_of_credits_upgrade_premium', source);
  useModalStore.getState().actions.openModal('PricingModal');
}

/**
 * True when an API / tool-run error means "not enough credits" (as opposed to
 * a model-access gate). Accepts ApiError-like objects and persisted
 * `toolRun.error` shapes (`{ code, message }`).
 */
export function isInsufficientCreditsError(error) {
  if (!error) return false;
  if (error.status === 402) return true;
  const code = String(
    error?.data?.reason
    || error?.data?.errorCode
    || error?.reason
    || error?.errorCode
    || error?.code
    || error?.type
    || '',
  ).toLowerCase();
  if (code === 'insufficient_credits' || code === 'credits_exhausted') return true;
  const message = String(error?.message || error?.data?.message || '');
  return /insufficient (purchased )?credits/i.test(message) || /not enough credits/i.test(message);
}

/** Convenience for non-hook callers (socket handlers, stores). */
export function getCurrentPlusCreditOptions() {
  return getPlusCreditOptions(useAuthStore.getState().user);
}
